import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient();

type Params = Parameters<typeof fetch>;

/**
 * A wrapper around {@link fetch} that ensures an OK response is sent back and
 * that we provide same-origin credentials for older safari versions
 */
export async function fetchOk(...args: Params) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  const resp = await fetch(args[0], {
    credentials: "same-origin",
    signal: controller.signal,
    ...args?.[1],
  });

  clearTimeout(timeoutId);

  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`failed to fetch ${args[0]}: ${body}`);
  }

  return resp;
}

/**
 * Wrapper around {@link fetchOk} that interprets a response as JSON
 */
export async function fetchJson<T = unknown>(...args: Params): Promise<T> {
  return fetchOk(...args).then((x) => x.json());
}

/**
 * Serializes a body as JSON and interprets the response as JSON
 */
export async function postJson<T = unknown>(
  url: Params[0],
  options: Omit<RequestInit, "body"> & { body: any }
): Promise<T> {
  return fetchJson<T>(url, {
    method: "POST",
    ...options,
    body: JSON.stringify(options.body),
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
}
